/**
 * Shipper Issues Page — Dedicated page listing all issues across containers.
 *
 * Provides a full table with filtering, search, sorting, pagination, row actions
 * (change status, more info, send email), and container details.
 */

import { useState, useMemo, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { parseDate } from '@internationalized/date';
import type { DateValue } from 'react-aria-components';
// RangeValue type for date ranges
type RangeValue<T> = { start: T; end: T } | null;
import { navigationStateFrom } from '@/types/navigation';
import {
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    XClose,
    AlertOctagon,
    File06,
    Globe02,
    Zap,
    SearchLg,
    ArrowDown,
    ChevronSelectorVertical,
    InfoCircle,
    Send01,
    Edit03,
} from '@untitledui/icons';
import { DialogTrigger, Popover } from 'react-aria-components';
import { DateRangePicker } from '@/components/application/date-picker/date-range-picker';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Toggle } from '@/components/base/toggle/toggle';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { Table, TableCard } from '@/components/application/table/table';
import { ChangeIssueStatusModal } from '@/components/application/containers/modals/change-issue-status-modal';
import { SendActivityEmailModal } from '@/components/application/containers/timeline/send-activity-email-modal';
import { useIssuesList, type IssuesListParams, type IssueListItem } from '@/hooks/api/use-issues-list';
import { useIssuesSummary } from '@/hooks/api/use-issues-summary';
import type { ContainerIssue } from '@/hooks/api/use-container-issues';
import type { SelectItemType } from '@/components/base/select/select';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

const CATEGORY_ITEMS: SelectItemType[] = [
    { id: '', label: 'All categories' },
    { id: 'document', label: 'Documenti' },
    { id: 'tracking', label: 'Tracking' },
    { id: 'non_document', label: 'Non-Document' },
    { id: 'system', label: 'Sistema' },
];

const ISSUE_TYPE_ITEMS: SelectItemType[] = [
    { id: '', label: 'All types' },
    // Document
    { id: 'missing_document', label: 'Missing Document' },
    { id: 'single_document_check', label: 'Single Doc Check' },
    { id: 'cross_check_document_check', label: 'Cross-Check' },
    { id: 'documentation_query', label: 'Documentation Query' },
    { id: 'missing_critical_data', label: 'Missing Critical Data' },
    { id: 'missing_critical_fields', label: 'Missing Critical Fields' },
    // Tracking
    { id: 'carrier_delay', label: 'Carrier Delay' },
    { id: 'tracking_not_found', label: 'Tracking Not Found' },
    { id: 'tracking_stale', label: 'Tracking Stale' },
];

const SEVERITY_ITEMS: SelectItemType[] = [
    { id: '', label: 'All severities' },
    { id: 'critical', label: 'Critical' },
    { id: 'high', label: 'High' },
    { id: 'medium', label: 'Medium' },
    { id: 'low', label: 'Low' },
];

const STATUS_ITEMS: SelectItemType[] = [
    { id: '', label: 'All statuses' },
    { id: 'open', label: 'Open' },
    { id: 'acknowledged', label: 'Acknowledged' },
    { id: 'in_progress', label: 'In Progress' },
    { id: 'pending_info', label: 'Pending Info' },
    { id: 'waiting_on_external', label: 'Waiting on External' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'false_positive', label: 'False Positive' },
    { id: 'wont_fix', label: "Won't Fix" },
];

// ============================================================================
// Date helpers
// ============================================================================

const formatDateToISO = (d: DateValue): string =>
    `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;

const parseISOToDateValue = (s: string | null): DateValue | null => {
    if (!s) return null;
    try { return parseDate(s); } catch { return null; }
};

const getDateRangeStrings = (range: RangeValue<DateValue> | null): { startDate: string | null; endDate: string | null } => {
    if (!range?.start || !range?.end) return { startDate: null, endDate: null };
    return { startDate: formatDateToISO(range.start), endDate: formatDateToISO(range.end) };
};

const SEVERITY_BADGE_COLOR: Record<string, 'error' | 'warning' | 'success' | 'gray'> = {
    critical: 'error',
    high: 'error',
    medium: 'warning',
    low: 'success',
};

const STATUS_BADGE_COLOR: Record<string, 'gray' | 'success' | 'warning' | 'error' | 'brand'> = {
    open: 'warning',
    acknowledged: 'brand',
    in_progress: 'brand',
    pending_info: 'gray',
    waiting_on_external: 'gray',
    resolved: 'success',
    false_positive: 'gray',
    wont_fix: 'gray',
};

const CONTAINER_STATUS_BADGE_COLOR: Record<string, 'gray' | 'success' | 'warning' | 'error' | 'brand'> = {
    draft: 'gray',
    booked: 'brand',
    in_transit: 'warning',
    arrived: 'success',
    delivered: 'success',
    on_hold: 'error',
    cancelled: 'gray',
};

// ============================================================================
// Issue Category
// ============================================================================

const DOCUMENT_ISSUE_TYPES = new Set([
    'missing_document', 'single_document_check', 'cross_check_document_check',
    'documentation_query', 'missing_critical_data', 'document_validation',
    'cross_check_failure', 'missing_critical_fields',
]);

const TRACKING_ISSUE_TYPES = new Set([
    'carrier_delay', 'weather_delay', 'tracking_not_found', 'tracking_stale',
]);

type IssueCategory = 'document' | 'tracking' | 'system';

function getIssueCategoryKey(issueType: string): IssueCategory {
    if (DOCUMENT_ISSUE_TYPES.has(issueType)) return 'document';
    if (TRACKING_ISSUE_TYPES.has(issueType)) return 'tracking';
    return 'system';
}

const CATEGORY_CONFIG: Record<IssueCategory, { label: string; icon: typeof File06; className: string }> = {
    document: { label: 'Documenti', icon: File06, className: 'text-brand-600 bg-brand-50' },
    tracking: { label: 'Tracking', icon: Globe02, className: 'text-warning-600 bg-warning-50' },
    system: { label: 'Sistema', icon: Zap, className: 'text-quaternary bg-secondary' },
};

// ============================================================================
// Helpers
// ============================================================================

function getRelativeTime(dateStr: string | null): string {
    if (!dateStr) return '-';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
}

function getRiskLabel(score: number | null): { text: string; color: 'error' | 'warning' | 'success' | 'gray' } {
    if (score === null) return { text: '-', color: 'gray' };
    if (score >= 0.75) return { text: 'Critical', color: 'error' };
    if (score >= 0.50) return { text: 'High', color: 'error' };
    if (score >= 0.25) return { text: 'Medium', color: 'warning' };
    return { text: 'Low', color: 'success' };
}

/** Convert IssueListItem to the ContainerIssue shape needed by ChangeIssueStatusModal */
function toContainerIssue(item: IssueListItem): ContainerIssue {
    return {
        issue_id: item.issue_id,
        issue_type: item.issue_type,
        issue_source: item.issue_source,
        title: item.title,
        description: item.description,
        severity: item.severity,
        priority: null,
        resolution_status: item.resolution_status,
        blocks_shipment: item.blocks_shipment,
        risk_contribution: null,
        issue_metadata: item.issue_metadata as ContainerIssue['issue_metadata'],
        created_at: item.created_at,
    };
}

// ============================================================================
// Sortable Header
// ============================================================================

type SortField = 'created_at' | 'severity' | 'status' | 'issue_type';

function SortableHeader({ label, field, currentSort, currentOrder, onSort }: {
    label: string;
    field: SortField;
    currentSort: string;
    currentOrder: string;
    onSort: (field: SortField) => void;
}) {
    const isActive = currentSort === field;
    return (
        <button
            onClick={() => onSort(field)}
            className="flex items-center gap-1 text-xs font-medium text-tertiary hover:text-primary transition-colors group"
        >
            {label}
            {isActive ? (
                <ArrowDown className={`size-3 stroke-[3px] text-quaternary ${currentOrder === 'asc' ? 'rotate-180' : ''}`} />
            ) : (
                <ChevronSelectorVertical className="size-3 stroke-[3px] text-quaternary opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
        </button>
    );
}

// ============================================================================
// More Info Popover
// ============================================================================

function IssueInfoPopover({ issue }: { issue: IssueListItem }) {
    const hasInfo = issue.description || issue.root_cause || issue.recommended_actions?.length || issue.resolution_notes || issue.issue_metadata;
    if (!hasInfo) return null;

    return (
        <DialogTrigger>
            <Button
                aria-label="Show issue details"
                className="flex items-center justify-center bg-transparent hover:bg-transparent p-0.5 text-tertiary hover:text-primary outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
            >
                <InfoCircle className="size-4" />
            </Button>
            <Popover
                aria-label="Issue details"
                placement="top start"
                offset={8}
                className={({ isEntering, isExiting }: { isEntering: boolean; isExiting: boolean }) =>
                    `origin-(--trigger-anchor-point) will-change-transform rounded-lg bg-primary shadow-lg ring-1 ring-secondary z-50 ${
                        isEntering
                            ? 'duration-150 ease-out animate-in fade-in placement-top:slide-in-from-bottom-0.5'
                            : isExiting
                                ? 'duration-100 ease-in animate-out fade-out placement-top:slide-out-to-bottom-0.5'
                                : ''
                    }`
                }
            >
                <div className="p-4 max-w-md space-y-3">
                    <p className="text-sm font-semibold text-primary">{issue.title}</p>

                    {issue.description && (
                        <div>
                            <p className="text-xs font-medium text-tertiary mb-0.5">Description</p>
                            <p className="text-sm text-primary whitespace-pre-wrap">{issue.description}</p>
                        </div>
                    )}

                    {issue.root_cause && (
                        <div>
                            <p className="text-xs font-medium text-tertiary mb-0.5">Root Cause</p>
                            <p className="text-sm text-primary">{issue.root_cause}</p>
                        </div>
                    )}

                    {issue.recommended_actions && issue.recommended_actions.length > 0 && (
                        <div>
                            <p className="text-xs font-medium text-tertiary mb-0.5">Recommended Actions</p>
                            <ul className="text-sm text-primary list-disc pl-4 space-y-0.5">
                                {issue.recommended_actions.map((action, i) => (
                                    <li key={i}>{action}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {issue.resolution_notes && (
                        <div>
                            <p className="text-xs font-medium text-tertiary mb-0.5">Resolution Notes</p>
                            <p className="text-sm text-primary">{issue.resolution_notes}</p>
                        </div>
                    )}

                    {issue.issue_source && (
                        <div>
                            <p className="text-xs font-medium text-tertiary mb-0.5">Source</p>
                            <p className="text-sm text-quaternary">{issue.issue_source.replace(/_/g, ' ')}</p>
                        </div>
                    )}
                </div>
            </Popover>
        </DialogTrigger>
    );
}

// ============================================================================
// Component
// ============================================================================

export function ShipperIssuesPage() {
    const { t } = useTranslation('pages:shipper-coordination');
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState(searchParams.get('search') ?? '');

    // Modals
    const [statusModalIssue, setStatusModalIssue] = useState<IssueListItem | null>(null);
    const [emailModalIssue, setEmailModalIssue] = useState<IssueListItem | null>(null);

    // Read filters from URL
    const severity = searchParams.get('severity') ?? '';
    const status = searchParams.get('status') ?? '';
    const category = searchParams.get('category') ?? '';
    const issueType = searchParams.get('issue_type') ?? '';
    const blockingOnly = searchParams.get('blocking') === 'true';
    const showResolved = searchParams.get('show_resolved') === 'true';
    const search = searchParams.get('search') ?? '';

    // Date range: initialize from URL params, synced back to URL
    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(() => {
        const sp = searchParams.get('start_date');
        const ep = searchParams.get('end_date');
        if (sp && ep) {
            const start = parseISOToDateValue(sp);
            const end = parseISOToDateValue(ep);
            if (start && end) return { start, end };
        }
        return null;
    });
    const { startDate, endDate } = useMemo(() => {
        const { startDate: sd, endDate: ed } = getDateRangeStrings(dateRange);
        return { startDate: sd ?? '', endDate: ed ?? '' };
    }, [dateRange]);

    const handleDateRangeChange = useCallback((value: RangeValue<DateValue> | null) => {
        setDateRange(value);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value?.start && value?.end) {
                next.set('start_date', formatDateToISO(value.start));
                next.set('end_date', formatDateToISO(value.end));
            } else {
                next.delete('start_date');
                next.delete('end_date');
            }
            next.delete('page');
            return next;
        });
    }, [setSearchParams]);
    const sortBy = (searchParams.get('sort_by') ?? 'created_at') as SortField;
    const sortOrder = searchParams.get('sort_order') ?? 'desc';
    const page = parseInt(searchParams.get('page') ?? '0', 10);

    const setFilter = useCallback((key: string, value: string) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (value) {
                next.set(key, value);
            } else {
                next.delete(key);
            }
            next.delete('page');
            return next;
        });
    }, [setSearchParams]);

    const setPage = useCallback((p: number) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (p > 0) {
                next.set('page', String(p));
            } else {
                next.delete('page');
            }
            return next;
        });
    }, [setSearchParams]);

    const handleSort = useCallback((field: SortField) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            const currentField = prev.get('sort_by') ?? 'created_at';
            const currentOrder = prev.get('sort_order') ?? 'desc';
            if (currentField === field) {
                next.set('sort_order', currentOrder === 'desc' ? 'asc' : 'desc');
            } else {
                next.set('sort_by', field);
                next.set('sort_order', 'desc');
            }
            next.delete('page');
            return next;
        });
    }, [setSearchParams]);

    const handleSearchSubmit = useCallback(() => {
        setFilter('search', searchInput.trim());
    }, [searchInput, setFilter]);

    const params: IssuesListParams = useMemo(() => ({
        severity: severity || undefined,
        status: status || undefined,
        category: category || undefined,
        issue_type: issueType || undefined,
        blocks_shipment: blockingOnly ? true : undefined,
        show_resolved: showResolved || undefined,
        search: search || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
    }), [severity, status, category, issueType, blockingOnly, showResolved, search, startDate, endDate, sortBy, sortOrder, page]);

    const { data, isLoading } = useIssuesList(params);
    const { data: summary } = useIssuesSummary({
        show_resolved: showResolved || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
    });

    const issues = data?.issues ?? [];
    const totalCount = data?.total_count ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const hasActiveFilters = !!(severity || status || category || issueType || blockingOnly || showResolved || search || startDate || endDate);

    const handleClearFilters = useCallback(() => {
        setSearchParams({});
        setSearchInput('');
        setDateRange(null);
    }, [setSearchParams]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-3">
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('issues.title')}</h1>
                        <p className="text-sm text-tertiary">
                            {t('issues.subtitle', { count: totalCount })}
                        </p>
                    </div>
                    <p className="text-sm text-secondary">{t('issues.intro')}</p>
                    <ul className="list-disc space-y-1.5 pl-5 text-sm text-tertiary">
                        <li>{t('issues.bulletTiles')}</li>
                        <li>{t('issues.bulletFilters')}</li>
                        <li>{t('issues.bulletTable')}</li>
                        <li>
                            <Trans
                                t={t}
                                i18nKey="issues.reviewContrast"
                                components={{
                                    reviewLink: (
                                        <Link
                                            to="/shipper/review-center"
                                            className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
                                        />
                                    ),
                                }}
                            />
                        </li>
                    </ul>
                </div>
                <ShipperOnboardingHeaderAction />
            </div>

            {/* Issue counts summary — clickable to filter the table */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <button
                    onClick={() => setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        if (issueType === 'missing_document') { next.delete('issue_type'); next.delete('category'); }
                        else { next.set('issue_type', 'missing_document'); next.set('category', 'document'); }
                        next.delete('page');
                        return next;
                    })}
                    className={`flex items-center gap-2 p-3 rounded-xl ring-1 transition-shadow cursor-pointer ${
                        issueType === 'missing_document'
                            ? 'ring-gray-400 dark:ring-gray-500 bg-gray-100 dark:bg-gray-800/60'
                            : 'ring-secondary bg-primary hover:ring-gray-300 dark:hover:ring-gray-600'
                    }`}
                >
                    <span className="text-2xl font-bold text-gray-700 dark:text-gray-300">{summary?.missing_document ?? 0}</span>
                    <span className="text-xs text-gray-600 dark:text-gray-400">Missing Docs</span>
                </button>
                <button
                    onClick={() => setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        if (issueType === 'single_document_check') { next.delete('issue_type'); next.delete('category'); }
                        else { next.set('issue_type', 'single_document_check'); next.set('category', 'document'); }
                        next.delete('page');
                        return next;
                    })}
                    className={`flex items-center gap-2 p-3 rounded-xl ring-1 transition-shadow cursor-pointer ${
                        issueType === 'single_document_check'
                            ? 'ring-brand-400 dark:ring-brand-500 bg-brand-50 dark:bg-brand-950/40'
                            : 'ring-secondary bg-primary hover:ring-brand-300 dark:hover:ring-brand-600'
                    }`}
                >
                    <span className="text-2xl font-bold text-brand-700 dark:text-brand-400">{summary?.single_document_check ?? 0}</span>
                    <span className="text-xs text-brand-600 dark:text-brand-500">Single Check</span>
                </button>
                <button
                    onClick={() => setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        if (issueType === 'cross_check_document_check') { next.delete('issue_type'); next.delete('category'); }
                        else { next.set('issue_type', 'cross_check_document_check'); next.set('category', 'document'); }
                        next.delete('page');
                        return next;
                    })}
                    className={`flex items-center gap-2 p-3 rounded-xl ring-1 transition-shadow cursor-pointer ${
                        issueType === 'cross_check_document_check'
                            ? 'ring-purple-400 dark:ring-purple-500 bg-purple-50 dark:bg-purple-950/40'
                            : 'ring-secondary bg-primary hover:ring-purple-300 dark:hover:ring-purple-600'
                    }`}
                >
                    <span className="text-2xl font-bold text-purple-700 dark:text-purple-400">{summary?.cross_check_document_check ?? 0}</span>
                    <span className="text-xs text-purple-600 dark:text-purple-500">Cross-Check</span>
                </button>
                <button
                    onClick={() => setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        if (category === 'non_document' && !issueType) { next.delete('category'); }
                        else { next.delete('issue_type'); next.set('category', 'non_document'); }
                        next.delete('page');
                        return next;
                    })}
                    className={`flex items-center gap-2 p-3 rounded-xl ring-1 transition-shadow cursor-pointer ${
                        category === 'non_document' && !issueType
                            ? 'ring-blue-400 dark:ring-blue-500 bg-blue-50 dark:bg-blue-950/40'
                            : 'ring-secondary bg-primary hover:ring-blue-300 dark:hover:ring-blue-600'
                    }`}
                >
                    <span className="text-2xl font-bold text-blue-700 dark:text-blue-400">{summary?.non_document ?? 0}</span>
                    <span className="text-xs text-blue-600 dark:text-blue-500">Other Issues</span>
                </button>
            </div>

            {/* Filter bar */}
            <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Date Range */}
                    <div className="flex items-center gap-1.5">
                        <div className="w-64 [&_button]:w-full">
                            <DateRangePicker
                                value={dateRange}
                                onChange={handleDateRangeChange}
                            />
                        </div>
                        <Tooltip
                            title="Filtro per data container"
                            description="Filtra per ETA/ETD dei container, non per data di creazione dell'issue. Coerente con i conteggi del dashboard."
                            placement="top"
                            arrow
                        >
                            <TooltipTrigger>
                                <InfoCircle className="size-4 shrink-0 text-quaternary hover:text-tertiary cursor-help" />
                            </TooltipTrigger>
                        </Tooltip>
                    </div>

                    {/* Search */}
                    <div className="w-64">
                        <Input
                            placeholder="Search title or container..."
                            value={searchInput}
                            onChange={(val) => setSearchInput(val)}
                            icon={SearchLg}
                            size="sm"
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter') handleSearchSubmit();
                            }}
                            onBlur={handleSearchSubmit}
                        />
                    </div>

                    {/* Severity */}
                    <div className="w-44">
                        <Select
                            placeholder="All severities"
                            items={SEVERITY_ITEMS}
                            selectedKey={severity}
                            onSelectionChange={(key) => setFilter('severity', (key as string) ?? '')}
                            size="sm"
                        >
                            {(item) => (
                                <Select.Item id={item.id} textValue={item.label}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>

                    {/* Status */}
                    <div className="w-48">
                        <Select
                            placeholder="All statuses"
                            items={STATUS_ITEMS}
                            selectedKey={status}
                            onSelectionChange={(key) => setFilter('status', (key as string) ?? '')}
                            size="sm"
                        >
                            {(item) => (
                                <Select.Item id={item.id} textValue={item.label}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>

                    {/* Category */}
                    <div className="w-44">
                        <Select
                            placeholder="All categories"
                            items={CATEGORY_ITEMS}
                            selectedKey={category}
                            onSelectionChange={(key) => setFilter('category', (key as string) ?? '')}
                            size="sm"
                        >
                            {(item) => (
                                <Select.Item id={item.id} textValue={item.label}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>

                    {/* Issue Type */}
                    <div className="w-48">
                        <Select
                            placeholder="All types"
                            items={ISSUE_TYPE_ITEMS}
                            selectedKey={issueType}
                            onSelectionChange={(key) => setFilter('issue_type', (key as string) ?? '')}
                            size="sm"
                        >
                            {(item) => (
                                <Select.Item id={item.id} textValue={item.label}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>

                    {/* Blocking toggle */}
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary ring-1 ring-secondary">
                        <Toggle
                            isSelected={blockingOnly}
                            onChange={(selected) => setFilter('blocking', selected ? 'true' : '')}
                            aria-label="Blocking only"
                            size="sm"
                        />
                        <span className="text-sm text-primary whitespace-nowrap">Blocking only</span>
                    </div>

                    {/* Show resolved toggle */}
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary ring-1 ring-secondary">
                        <Toggle
                            isSelected={showResolved}
                            onChange={(selected) => setFilter('show_resolved', selected ? 'true' : '')}
                            aria-label="Show resolved"
                            size="sm"
                        />
                        <span className="text-sm text-primary whitespace-nowrap">Show resolved</span>
                    </div>

                    {/* Clear */}
                    {hasActiveFilters && (
                        <Button
                            size="sm"
                            color="secondary"
                            iconLeading={XClose}
                            onClick={handleClearFilters}
                        >
                            Clear
                        </Button>
                    )}
                </div>
            </div>

            {/* Table */}
            <TableCard.Root>
                <Table>
                    <Table.Header>
                        <Table.Head label="Container" isRowHeader />
                        <Table.Head label="Issue" />
                        <Table.Head>
                            <SortableHeader label="Category" field="issue_type" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                        </Table.Head>
                        <Table.Head>
                            <SortableHeader label="Severity" field="severity" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                        </Table.Head>
                        <Table.Head>
                            <SortableHeader label="Status" field="status" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                        </Table.Head>
                        <Table.Head label="Blocks" />
                        <Table.Head label="Risk" />
                        <Table.Head>
                            <SortableHeader label="Created" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                        </Table.Head>
                        <Table.Head label="" />
                    </Table.Header>
                    <Table.Body>
                        {isLoading && issues.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={9}>
                                    <div className="flex items-center justify-center py-12">
                                        <div className="animate-pulse flex items-center gap-2">
                                            <div className="h-4 w-4 bg-tertiary/30 rounded-full" />
                                            <div className="h-4 w-32 bg-tertiary/30 rounded" />
                                        </div>
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ) : issues.length === 0 ? (
                            <Table.Row>
                                <Table.Cell colSpan={9}>
                                    <div className="flex flex-col items-center justify-center py-12 px-4">
                                        <div className="rounded-full bg-success-50 p-3 mb-4">
                                            <AlertCircle className="size-6 text-success-600" />
                                        </div>
                                        <p className="text-sm text-tertiary text-center">
                                            No issues found
                                        </p>
                                        {hasActiveFilters && (
                                            <p className="text-xs text-quaternary text-center mt-1">
                                                Try adjusting your filters
                                            </p>
                                        )}
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ) : (
                            issues.map(issue => {
                                const risk = getRiskLabel(issue.container_risk_score);
                                const isResolved = ['resolved', 'false_positive', 'wont_fix'].includes(issue.resolution_status);
                                const catKey = getIssueCategoryKey(issue.issue_type);
                                const cat = CATEGORY_CONFIG[catKey];
                                const CatIcon = cat.icon;
                                return (
                                    <Table.Row
                                        key={issue.issue_id}
                                        className={`cursor-pointer hover:bg-secondary ${isResolved ? 'opacity-60' : ''}`}
                                        onClick={() => navigate(
                                            `/shipper/containers/${issue.container_id}?tab=issues`,
                                            { state: navigationStateFrom(location) }
                                        )}
                                    >
                                        {/* Container */}
                                        <Table.Cell>
                                            <div className="flex flex-col gap-0.5">
                                                <span className="font-medium text-primary text-sm">
                                                    {issue.container_number ?? 'N/A'}
                                                </span>
                                                {issue.container_status && (
                                                    <Badge color={CONTAINER_STATUS_BADGE_COLOR[issue.container_status] ?? 'gray'} size="sm">
                                                        {issue.container_status.replace(/_/g, ' ')}
                                                    </Badge>
                                                )}
                                            </div>
                                        </Table.Cell>

                                        {/* Issue Title */}
                                        <Table.Cell>
                                            <div className="flex items-center gap-1.5">
                                                <span className={`text-sm ${isResolved ? 'text-tertiary line-through' : 'text-primary'}`}>
                                                    {issue.title}
                                                </span>
                                                <IssueInfoPopover issue={issue} />
                                            </div>
                                        </Table.Cell>

                                        {/* Category & Type */}
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                <div className={`flex items-center justify-center size-6 rounded shrink-0 ${cat.className}`}>
                                                    <CatIcon className="size-3.5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="text-xs font-medium text-primary block">{cat.label}</span>
                                                    <span className="text-xs text-quaternary block truncate">
                                                        {issue.issue_type.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            </div>
                                        </Table.Cell>

                                        {/* Severity */}
                                        <Table.Cell>
                                            <Badge color={SEVERITY_BADGE_COLOR[issue.severity] ?? 'gray'} size="sm">
                                                {issue.severity}
                                            </Badge>
                                        </Table.Cell>

                                        {/* Status */}
                                        <Table.Cell>
                                            <Badge color={STATUS_BADGE_COLOR[issue.resolution_status] ?? 'gray'} size="sm">
                                                {issue.resolution_status.replace(/_/g, ' ')}
                                            </Badge>
                                        </Table.Cell>

                                        {/* Blocks */}
                                        <Table.Cell>
                                            {issue.blocks_shipment ? (
                                                <AlertOctagon className="size-4 text-error-500" />
                                            ) : (
                                                <span className="text-xs text-quaternary">-</span>
                                            )}
                                        </Table.Cell>

                                        {/* Risk */}
                                        <Table.Cell>
                                            <Badge color={risk.color} size="sm">
                                                {risk.text}
                                                {issue.container_risk_score !== null && (
                                                    <span className="ml-1 opacity-70">
                                                        {Math.round(issue.container_risk_score * 100)}%
                                                    </span>
                                                )}
                                            </Badge>
                                        </Table.Cell>

                                        {/* Created */}
                                        <Table.Cell>
                                            <span className="text-xs text-tertiary">
                                                {getRelativeTime(issue.created_at)}
                                            </span>
                                        </Table.Cell>

                                        {/* Actions */}
                                        <Table.Cell>
                                            <div onClick={e => e.stopPropagation()}>
                                                <Dropdown.Root>
                                                    <Dropdown.DotsHorizontalButton />
                                                    <Dropdown.Popover className="w-min">
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item
                                                                id="change-status"
                                                                icon={Edit03}
                                                                onAction={() => setStatusModalIssue(issue)}
                                                            >
                                                                Change Status
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                id="send-email"
                                                                icon={Send01}
                                                                onAction={() => setEmailModalIssue(issue)}
                                                            >
                                                                Send Email
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown.Popover>
                                                </Dropdown.Root>
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })
                        )}
                    </Table.Body>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-3 border-t border-secondary">
                        <span className="text-sm text-tertiary">
                            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
                        </span>
                        <div className="flex items-center gap-1">
                            <Button
                                size="sm"
                                color="secondary"
                                iconLeading={ChevronLeft}
                                onClick={() => setPage(Math.max(0, page - 1))}
                                isDisabled={page === 0}
                            />
                            <span className="text-sm text-tertiary px-2">
                                Page {page + 1} of {totalPages}
                            </span>
                            <Button
                                size="sm"
                                color="secondary"
                                iconLeading={ChevronRight}
                                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                                isDisabled={page >= totalPages - 1}
                            />
                        </div>
                    </div>
                )}
            </TableCard.Root>

            {/* Change Status Modal */}
            {statusModalIssue && (
                <ChangeIssueStatusModal
                    isOpen={!!statusModalIssue}
                    onClose={() => setStatusModalIssue(null)}
                    issue={toContainerIssue(statusModalIssue)}
                    containerId={statusModalIssue.container_id}
                    containerNumber={statusModalIssue.container_number ?? 'N/A'}
                />
            )}

            {/* Send Email Modal */}
            {emailModalIssue && (
                <SendActivityEmailModal
                    isOpen={!!emailModalIssue}
                    onClose={() => setEmailModalIssue(null)}
                    issueId={emailModalIssue.issue_id}
                    issueTitle={emailModalIssue.title}
                    issueSummary={emailModalIssue.description ?? ''}
                    containerId={emailModalIssue.container_id}
                />
            )}
        </div>
    );
}
